import React, { useState, useEffect, useContext } from "react";
import { Card, Table, Button, Form, Tooltip, OverlayTrigger} from "react-bootstrap";
import NotyfContext from "../contexts/NotyfContext";
import { X, Check, Edit2, Trash2, File} from "react-feather";
import { UserSettingsContext } from "../contexts/UserSettingsContext";
import { useTable, useSortBy, useFilters} from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";

import authenticatedFetch from "../utils/fetch"
import { caseInsensitiveSort } from "./reactTableUtils";
import EditableExpires from "./EditableExpires";
import LinkedDocumentsDisplay from "./LinkedDocumentsDisplay";
import LinkedDocumentSelect from "./LinkedDocumentSelect";
import { getDocumentDisplayLabel } from "./linkedDocumentUtils";

const Training = ({ id, providerId, type, organization, startDate, endDate, notes, documents, refreshList, canEdit, onCancelEdit, startInEditMode=false }) => {
  const [editing, setEditing] = useState(startInEditMode);
  const [localType, setLocalType] = useState(type);
  const [localOrganization, setLocalOrganization] = useState(organization);
  const [localStartDate, setLocalStartDate] = useState(startDate);
  const [localEndDate, setLocalEndDate] = useState(endDate);
  const [localNotes, setLocalNotes] = useState(notes);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [documentFilenames, setDocumentFilenames] = useState([]);
  
  const { isH3Admin, authorities, documentsRefresh, setDocumentsRefresh } = useContext(UserSettingsContext);

  const notyf = useContext(NotyfContext);

  const setInitialDocumentFilenames = () => {
    let initialDocumentFilenames = []
    let initialSelectedDocuments = []
    if (documents) // undefined if we're excluding the column due to permissions
    {
      for (var i=0; i<documents.length; i++)
      {
        initialDocumentFilenames.push(documents[i].filename);
        initialSelectedDocuments.push({value: documents[i].filename, label: getDocumentDisplayLabel(documents[i])});
      }
    }

    setDocumentFilenames(initialDocumentFilenames);
    setSelectedDocuments(initialSelectedDocuments);
  }

  useEffect(() => {
      setInitialDocumentFilenames();
  }, [id, providerId, documents]);

  useEffect(() => {
    if (!editing) {
      setLocalNotes(notes);
      setLocalStartDate(startDate);
      setLocalEndDate(endDate);
      setInitialDocumentFilenames();
    }
  }, [startDate, endDate, notes, documents]);

  const handleDocumentChanged = (documents, e) => {
    setSelectedDocuments(documents);

    let newDocumentFilenames = []
    for (var i=0; i<documents.length; i++)
    {
      newDocumentFilenames.push(documents[i].value);
    }

    setDocumentFilenames(newDocumentFilenames);
  }

  function handleDeleteClicked(e) {
    e.preventDefault();
    if (window.confirm("Are you sure you want to delete this item?"))
    {
      authenticatedFetch({
        method: "DELETE",
        path: "training/" + id,
        successHandler: function(result) {
          refreshList();
          setDocumentsRefresh(documentsRefresh + 1);
          notyf.open({type: "success", message: "Deleted!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
        },
        errorHandler: function(error) {
          notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
        }
      });
    }
  }

  function handleEditClicked(e) {
    e.preventDefault();
    setEditing(true);
  }

  function cancelEdit(e) {
    e.preventDefault();
    setEditing(false);
    setLocalType(type);
    setLocalOrganization(organization);
    setLocalStartDate(startDate);
    setLocalEndDate(endDate);
    setLocalNotes(notes);
    setInitialDocumentFilenames();

    if (onCancelEdit)
    {
      onCancelEdit();
    }
  }

  function saveEdit(e) {
    e.preventDefault();

    if (localType === '' && localOrganization === '')
    {
      alert("Please enter a type or organization");
      return;
    }

    setEditing(false);

    authenticatedFetch({
      method: id ? "PUT" : "POST",
      requestData: {providerId: parseInt(providerId), type: localType, organization: localOrganization, startDate: localStartDate, endDate: localEndDate, notes: localNotes, documentFilenames: documentFilenames},
      path: id ? "training/" + id  : "training",
      successHandler: function(result) {
        refreshList();
        setDocumentsRefresh(documentsRefresh + 1);
        notyf.open({type: "success", message: id ? "Updated!" : "Saved!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
      },
      errorHandler: function() {
        setEditing(true);
        setLocalType(type);
        setLocalOrganization(organization);
        setLocalStartDate(startDate);
        setLocalEndDate(endDate);
        setLocalNotes(notes);
        setInitialDocumentFilenames();
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }

  function handleTypeChanged(e) {
    e.preventDefault();
    setLocalType(e.target.value);
  }

  function handleOrganizationChanged(e) {
    e.preventDefault();
    setLocalOrganization(e.target.value);
  }

  function handleStartDateChanged(val, e) {
    e.preventDefault();
    setLocalStartDate(val);
  }

  function handleEndDateChanged(val, e) {
    e.preventDefault();
    setLocalEndDate(val);
  }

  function handleNotesChanged(e) {
    e.preventDefault();
    setLocalNotes(e.target.value);
  }

  var notesReadOnly = '';
  if (localNotes && localNotes.length > 0) {
    notesReadOnly = localNotes.split("\n").map(function(item, idx) {
      return ( 
          <span key={idx}>
              {item}
              <br />
          </span>
       )
    });
  }

  return (
    <>
    {editing ?
    <td>
      <Form.Control size="sm" autoFocus={true} maxLength={255} value={localType} onChange={(e) => {handleTypeChanged(e)}} />
    </td>
    :
    <td style={
      !localType || localType.length === 0
        ? {
            backgroundColor: "rgba(220,53,69,0.3)",
            // cancel the striped bg for this cell
            ["--bs-table-accent-bg"]: "transparent"
          }
        : {}
    }>
      {localType}
    </td>
    }
    {editing ?
    <td>
      <Form.Control size="sm" maxLength={255} value={localOrganization} onChange={(e) => {handleOrganizationChanged(e)}} />
    </td>
    :
    <td>
      {localOrganization}
    </td>
    }
    <td>
      <EditableExpires warnWithColors={false} localExpires={localStartDate} localActive={true} editingExpires={editing} handleExpiresChanged={handleStartDateChanged} />
    </td>
    <td>
      <EditableExpires warnWithColors={false} localExpires={localEndDate} localActive={true} editingExpires={editing} handleExpiresChanged={handleEndDateChanged} />
    </td>
    {editing ?
    <td>
      <Form.Control style={{height: "120px"}} size="sm" as="textarea" maxLength={511} value={localNotes} onChange={(e) => {handleNotesChanged(e)}} />
    </td>
    :
    <td>
      {notesReadOnly}
    </td>
    }
    {(isH3Admin || (authorities.includes("PROVIDER_DOCUMENT_VIEW") || authorities.includes("PROVIDER_DOCUMENT_EDIT")))&&
    <>
      {editing && (isH3Admin || authorities.includes("PROVIDER_DOCUMENT_EDIT"))?
      <td>
        <LinkedDocumentSelect entityType={"training"} providerId={providerId} handleDocumentChanged={handleDocumentChanged} selectedDocuments={selectedDocuments}/>
      </td>
        : 
      <td>
        <LinkedDocumentsDisplay entityType={"training"} documents={documents} entityId={id}/>
      </td> 
      }
    </>
    }
    <td style={{width: '70px'}}>
      { canEdit?
        <>
        {editing ? 
        <>
          <X style={{'height': '16px', 'width': '16px'}} cursor="pointer" onClick={(e) => {cancelEdit(e)}} />
          &nbsp;<Check style={{'height': '16px', 'width': '16px'}} cursor="pointer" onClick={(e) => {saveEdit(e)}} className="text-success"/>
        </>
        :
        <>
          <Edit2 style={{'height': '12px', 'width': '12px'}} cursor="pointer" onClick={(e) => {handleEditClicked(e)}}/>&nbsp;&nbsp;
          <Trash2 style={{'height': '12px', 'width': '12px'}} cursor="pointer" onClick={(e) => {handleDeleteClicked(e)}}/>
        </>
        }
        </>
        : <>&nbsp;</>
      }
    </td>
  </>
  )
}


const TrainingsTable = ({providerId, addingNew, onCancelAddNew, list, isLoaded, error, canEdit, refreshList}) => {
  const data = list;

  const { isH3Admin, authorities } = useContext(UserSettingsContext);

  const columns = React.useMemo(() => {
    const baseColumns = [
      {
        accessor: "id",
        key: "id",
      },
      {
        Header: "Type",
        accessor: "type",
        key: "type",
      },
      {
        Header: "Organization",
        accessor: "organization",
        key: "organization",
      },
      {
        Header: "Start Date",
        accessor: "startDate",
        key: "startDate",
      },
      {
        Header: "End Date",
        accessor: "endDate",
        key: "endDate",
      },
      {
        Header: "Details",
        accessor: "notes",
        key: "notes",
      }
    ];

    if(isH3Admin || (authorities.includes("PROVIDER_DOCUMENT_VIEW") || authorities.includes("PROVIDER_DOCUMENT_EDIT")))
    {
      baseColumns.push(
      {
        Header: () => (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Documents</Tooltip>}
          >
            <span>
              <File size={16} />
            </span>
          </OverlayTrigger>
        ),
        accessor: "documents",
        key: "documents",
      });
    }

    baseColumns.push(
      {
        Header: "",
        accessor: "icons",
        key: "icons",
      },
    );
    return baseColumns;
  }, [isH3Admin, authorities]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
  useTable(
    {
      columns,
      data,
      autoResetSortBy: false,
      autoResetFilters: false,
      initialState: {
        hiddenColumns: ['id'],
        sortBy: [
          {
            id: 'startDate',
            desc: true
          },
          {
            id: 'endDate',
            desc: true
          }
        ],
      },
      sortTypes: {
        alphanumeric: (row1, row2, columnName) => caseInsensitiveSort({row1, row2, columnName})
      }
    },
    useFilters,
    useSortBy,
  );

  return (
    <Table className="table-sm" striped bordered hover {...getTableProps()}>
    <thead>
      {headerGroups.map((headerGroup) => (
        <tr {...headerGroup.getHeaderGroupProps()}>
          {headerGroup.headers.map((column) => {
            const { key, ...restHeaderProps } = (column.id === "icons" || column.id === "notes" || column.id === "documents") ? column.getHeaderProps() : column.getHeaderProps(column.getSortByToggleProps());
            return (
              <th {...restHeaderProps} key={key}>
                {column.render("Header")}
                {(column.id !== "icons" && column.id !== "notes" && column.id !== "documents") ? (
                  <>
                  {column.isSorted ? (
                      column.isSortedDesc ? (
                        <FontAwesomeIcon icon={faSortDown} className="ms-2" />
                      ) : (
                        <FontAwesomeIcon icon={faSortUp} className="ms-2" />
                      )
                    ) : (
                      <FontAwesomeIcon icon={faSort} className="ms-2" />
                  )}
                  </>
                ) : <></>
                }
              </th>
            )
          }
          )}
        </tr>
      ))}
    </thead>
    <tbody {...getTableBodyProps()}>
      {
      error ? (<tr><td colSpan={4}>Something went wrong; please reload the page...</td></tr>) : ( 
        !isLoaded ? (<tr><td colSpan={4}>Loading...</td></tr>) : (
          rows.map((row) => {
          prepareRow(row);
          const { key, ...restRowProps } = row.getRowProps();
          return (
            <tr {...restRowProps} key={key}>
              <Training providerId={providerId} id={row.values.id} key={row.values.id} type={row.values.type ? row.values.type : ""} organization={row.values.organization ? row.values.organization : ""} startDate={row.values.startDate} endDate={row.values.endDate} notes={row.values.notes ? row.values.notes : ""} documents={row.values.documents}  refreshList={refreshList} canEdit={canEdit}/>
            </tr>
          );
        })))
      }
      {
      addingNew ?
        <tr><Training providerId={providerId} key={0} startInEditMode={true} type={""} organization={""} startDate={null} endDate={null} notes={""} documents={[]} onCancelEdit={onCancelAddNew} canEdit={canEdit} refreshList={refreshList}/></tr>
      : <></>
      }
    </tbody>
  </Table>
  )
}

const Trainings = ({providerId, canEdit}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);
  const [addingNew, setAddingNew] = useState(false);

  const { documentsRefresh, tasksRefresh } = useContext(UserSettingsContext);

  useEffect(() => {
    refreshList();
  }, [providerId, documentsRefresh, tasksRefresh])

  function refreshList()
  {
    setAddingNew(false);
    authenticatedFetch({
      path: "training/provider/" + providerId,
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
      },
      errorHandler: function() {
        setIsLoaded(true);
        setError(true);
      }
    });
  }

  const handleAddNewClicked = (e) => {
    e.preventDefault();
  
    setAddingNew(true);
  }

  const onCancelAddNew = () => {
    setAddingNew(false);
  }

  var list = [];
  if (isLoaded)
  {
    list = data.map((item, i) => {
      return {key: item.id, id: item.id, type: item.type, organization: item.organization, startDate: item.startDate, endDate: item.endDate, notes: item.notes, documents: item.documents, updated: item.updated, updatedBy: item.updatedBy}
    }, this);
  }

  return (
    <Card>
      <Card.Header style={{marginBottom: '-20px'}}>
        <Card.Title>Education and Training
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <TrainingsTable list={list} addingNew={addingNew} onCancelAddNew={onCancelAddNew} providerId={providerId} error={error} isLoaded={isLoaded} canEdit={canEdit} refreshList={refreshList}/>
        {canEdit?
          <span className="float-end" style={{marginTop: '6px'}}>
            <Button size="sm" variant="primary" className="float-end" onClick={(e) => {handleAddNewClicked(e)}}>+ Add New Education</Button>
          </span>
          : <></>
        }
      </Card.Body>
    </Card>
  )
}

export default Trainings;
